import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../Config/config";

const DEFAULT_AVATAR = "/user image.webp";

const AVATARS = [
  DEFAULT_AVATAR,
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Felix",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Gizmo",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Milo",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Luna",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Simba",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Nala",
  "https://api.dicebear.com/7.x/avataaars/svg?seed=Oscar",
  "/malelogo1.png",
  "/malelogo2.png",
  "/malelogo3.png",
  "/femalelogo1.png",
  "/femalelogo2.png",
  "/femalelogo3.png",
];

/** Persist avatar/image to localStorage so TenantHeader and CleanProfile pick it up. */
const persistAvatar = (dataUrlOrPath) => {
  localStorage.setItem("selectedAvatar", dataUrlOrPath);
  try {
    const current = JSON.parse(localStorage.getItem("user") || "{}");
    localStorage.setItem("user", JSON.stringify({ ...current, profile_image: dataUrlOrPath }));
  } catch { /* non-critical */ }
};

const EditProfile = () => {
  const navigate    = useNavigate();
  const fileInputRef = useRef(null);

  const [user, setUser]     = useState({});
  const [form, setForm]     = useState({
    first_name: "",
    last_name: "",
    email: "",
    userId: "",
    college_name: "",
    avatar_url: DEFAULT_AVATAR,
  });
  const [status, setStatus]       = useState("");
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    const storedUser = JSON.parse(localStorage.getItem("user") || "null");
    if (storedUser) {
      setUser(storedUser);

      let firstName = storedUser.first_name || "";
      let lastName  = storedUser.last_name  || "";

      // Student tenant may only carry a single 'name' field
      if (storedUser.role === "student" && storedUser.name && !firstName && !lastName) {
        const parts = storedUser.name.split(" ");
        firstName = parts[0];
        lastName  = parts.slice(1).join(" ");
      }

      // Resolve current avatar: uploaded image > saved avatar > stored url > default
      const currentAvatar =
        localStorage.getItem("selectedAvatar") ||
        storedUser.profile_image ||
        storedUser.avatar_url ||
        DEFAULT_AVATAR;

      setForm({
        first_name:   firstName,
        last_name:    lastName,
        email:        storedUser.email        || "",
        userId:       storedUser.userId       || "",
        college_name: storedUser.college_name || "",
        avatar_url:   currentAvatar,
      });
    }
  }, []);

  /* -- Handlers -- */

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  /** Select a preset avatar - persist immediately. */
  const selectAvatar = (url) => {
    setForm((f) => ({ ...f, avatar_url: url }));
    persistAvatar(url);
  };

  /** Upload a custom image — sends to S3 via the upload-profile-image endpoint. */
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setStatus("❌ Please select a valid image file.");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setStatus("❌ Image must be smaller than 2 MB.");
      return;
    }

    // Show a local blob preview immediately while the upload is in flight
    const previousAvatar = form.avatar_url;
    const localPreview   = URL.createObjectURL(file);
    setForm((f) => ({ ...f, avatar_url: localPreview }));
    setUploading(true);
    e.target.value = "";

    try {
      const storedUser = JSON.parse(localStorage.getItem("user") || "{}");
      const fd = new FormData();
      fd.append("image", file);
      fd.append("userId",      storedUser.userId       || storedUser.id    || "");
      fd.append("collegeName", storedUser.college_name || "");
      fd.append("role",        storedUser.role         || "");

      const res = await axios.post(`${API_BASE_URL}/user/upload-profile-image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      const s3Url = res.data?.imageUrl;
      if (s3Url) {
        setForm((f) => ({ ...f, avatar_url: s3Url }));
        persistAvatar(s3Url);
        setStatus("✅ Profile picture uploaded!");
      } else {
        // Upload returned no URL — restore previous avatar
        setForm((f) => ({ ...f, avatar_url: previousAvatar }));
        setStatus("❌ Upload succeeded but no URL returned.");
      }
    } catch (err) {
      console.error("Profile image upload failed:", err);
      // Restore previous avatar so no blob URL leaks into localStorage
      setForm((f) => ({ ...f, avatar_url: previousAvatar }));
      setStatus("⚠️ Upload failed. Please try again.");
    } finally {
      setUploading(false);
      URL.revokeObjectURL(localPreview);
    }
  };

  /** Save text fields to backend; also persist locally regardless of API outcome. */
  const handleSave = async () => {
    if (
      !form.first_name ||
      !form.last_name  ||
      !form.email      ||
      !form.userId     ||
      !form.college_name
    ) {
      setStatus("❌ All fields are required.");
      return;
    }

    // Always save locally first so changes survive even if the API is slow/down
    try {
      const current = JSON.parse(localStorage.getItem("user") || "{}");
      localStorage.setItem(
        "user",
        JSON.stringify({
          ...current,
          first_name:   form.first_name,
          last_name:    form.last_name,
          name:         `${form.first_name} ${form.last_name}`.trim(),
          email:        form.email,
          college_name: form.college_name,
          avatar_url:   form.avatar_url,
          profile_image: form.avatar_url,
        })
      );
    } catch { /* non-critical */ }

    // Use the persisted S3 URL for the backend call — never send a local blob URL
    const persistedAvatar = localStorage.getItem("selectedAvatar") || null;
    const avatarForBackend = (persistedAvatar && !persistedAvatar.startsWith("blob:"))
      ? persistedAvatar
      : null;

    try {
      const res = await axios.put(`${API_BASE_URL}/user/update-profile`, {
        id: user.id,
        ...form,
        avatar_url:    avatarForBackend,
        profile_image: avatarForBackend,
        role: user.role,
      });

      if (res.data?.user) {
        const merged = {
          ...res.data.user,
          userId:        res.data.user.userId || res.data.user.roll_number || res.data.user.user_id || form.userId,
          profile_image: avatarForBackend || res.data.user.profile_image,
          avatar_url:    avatarForBackend || res.data.user.avatar_url,
        };
        localStorage.setItem("user", JSON.stringify(merged));
      }

      setStatus("✅ Profile updated successfully!");
    } catch {
      // Local save already done above - treat backend failure as a soft warning
      setStatus("✅ Profile saved locally. (Server sync failed - changes will persist on this device.)");
    }

    setTimeout(() => navigate("/profile"), 1800);
  };

  /* -- Render -- */

  return (
    <div className="min-h-screen flex justify-center items-center bg-bgMain text-body p-6">
      <div className="premium-card w-full max-w-2xl p-8 md:p-12 relative overflow-hidden">

        {/* Brand accent border */}
        <div className="absolute top-0 left-0 w-full h-2 bg-gradient-to-r from-primary via-secondary to-accent" />

        <div className="flex items-center justify-between mb-10 relative z-10">
          <button 
            onClick={() => navigate(-1)} 
            className="flex items-center gap-2 text-primary font-black text-sm hover:translate-x-[-4px] transition-transform"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            BACK
          </button>
          <h2 className="text-3xl font-extrabold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent uppercase tracking-tighter">
            Edit Profile
          </h2>
          <div className="w-10"></div> {/* Spacer to keep title centered */}
        </div>

        {/* -- Current avatar preview + upload button -- */}
        <div className="flex items-center gap-5 mb-8 relative z-10">
          <div className="relative flex-shrink-0">
            <img
              src={form.avatar_url}
              alt="Current avatar"
              className="w-20 h-20 rounded-full object-contain bg-white border-4 shadow-lg"
              style={{ borderColor: "var(--brand-primary)" }}
              onError={(e) => { e.currentTarget.src = DEFAULT_AVATAR; }}
            />
            {uploading && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center"
                   style={{ background: "rgba(0,0,0,0.45)" }}>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              </div>
            )}
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted mb-2">
              Profile Picture
            </p>
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="px-6 py-2.5 rounded-xl text-sm font-black transition-all hover:scale-105 active:scale-95 disabled:opacity-50 shadow-md uppercase tracking-wider"
              style={{
                background: "linear-gradient(135deg, var(--brand-primary), var(--brand-secondary))",
                color: "white",
              }}
            >
              📷 Upload Photo
            </button>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={handleImageUpload}
            />
            <p className="text-2xs text-muted mt-1.5">JPG, PNG, WebP - max 2 MB</p>
          </div>
        </div>

        {/* -- Preset avatar grid -- */}
        <div className="mb-10 relative z-10">
          <label className="block text-muted font-black text-xs uppercase tracking-[0.2em] mb-4">
            Or Choose a Preset Avatar
          </label>
          <div className="grid grid-cols-5 sm:grid-cols-8 gap-3">
            {AVATARS.map((url, idx) => (
              <div
                key={idx}
                onClick={() => selectAvatar(url)}
                className={`cursor-pointer rounded-full p-1 transition-all duration-300 ${
                  form.avatar_url === url
                    ? "bg-primary scale-110 shadow-lg shadow-primary/20"
                    : "bg-transparent hover:bg-primary/10"
                }`}
              >
                <img
                  src={url}
                  alt="Avatar option"
                  className="w-full aspect-square rounded-full bg-white border border-accentGray/20 object-cover"
                />
              </div>
            ))}
          </div>
        </div>

        {/* -- Text fields -- */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 relative z-10">
          {["first_name", "last_name", "email", "userId", "college_name"].map((field) => (
            <div key={field} className={field === "email" || field === "college_name" ? "md:col-span-2" : ""}>
              <label className="block text-muted font-bold text-xs uppercase tracking-widest mb-2">
                {field.replace(/_/g, " ")}
              </label>
              <input
                type="text"
                name={field}
                value={form[field]}
                onChange={handleChange}
                // userId and email are identifiers - show them but lock them
                readOnly={field === "userId" || field === "email"}
                className="w-full p-4 rounded-2xl bg-[#F9F7FF] border-2 border-accentGray/20 text-heading focus:border-primary focus:ring-4 focus:ring-primary/10 outline-none transition-all font-bold shadow-sm"
                style={
                  field === "userId" || field === "email"
                    ? { opacity: 0.6, cursor: "not-allowed" }
                    : {}
                }
                placeholder={`Enter your ${field.replace(/_/g, " ")}`}
              />
            </div>
          ))}
        </div>

        {/* -- Status banner -- */}
        {status && (
          <div
            className={`mb-8 p-4 rounded-2xl border ${
              status.startsWith("✅")
                ? "bg-green-500/10 border-green-500/30 text-green-600"
                : "bg-red-500/10 border-red-500/30 text-red-600"
            }`}
          >
            <p className="text-sm font-bold text-center">{status}</p>
          </div>
        )}

        {/* -- Actions -- */}
        <div className="flex flex-col md:flex-row gap-4 relative z-10 pt-4">
          <button
            onClick={handleSave}
            className="flex-1 btn-primary py-4 rounded-2xl font-black text-lg shadow-xl hover:scale-[1.02] active:scale-95 transition-all uppercase tracking-widest"
          >
            Save Changes
          </button>
          <button
            onClick={() => navigate(-1)}
            className="flex-1 px-8 py-4 bg-[#F9F7FF] text-muted border-2 border-accentGray/20 rounded-2xl font-black hover:bg-bgMain transition-all uppercase text-sm tracking-widest shadow-sm"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditProfile;

